import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Filter, Plus, Edit, Trash2, Star, Image as ImageIcon, Video,
  X, Check, UploadCloud, Play, Loader2, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ratingOptions = [1, 2, 3, 4, 5];

function StarsRow({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange?.(n)}
          className={`p-0.5 ${onChange ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          data-testid={`star-${n}`}
        >
          <Star
            className={`h-4 w-4 ${n <= value ? 'text-[#C4A35A] fill-[#C4A35A]' : 'text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );
}

function MediaPreview({ media, onRemove }) {
  if (!media || media.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="media-preview">
      {media.map((m, idx) => (
        <div key={idx} className="relative w-20 h-20 rounded-md overflow-hidden border border-gray-200 bg-gray-50 group">
          {m.type === 'video' ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-900">
              <Play className="h-6 w-6 text-white" />
              <span className="absolute bottom-0 left-0 right-0 text-[9px] bg-black/60 text-white text-center py-0.5">VÍDEO</span>
            </div>
          ) : (
            <img src={m.url} alt={`mídia ${idx + 1}`} className="w-full h-full object-cover" />
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="absolute top-0.5 right-0.5 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`media-remove-${idx}`}
              aria-label="Remover mídia"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ReviewFormDialog({ open, onOpenChange, products, initial, onSaved }) {
  const isEdit = !!initial?.review_id;
  const [productId, setProductId] = useState(initial?.product_id || '');
  const [userName, setUserName] = useState(initial?.user_name || '');
  const [rating, setRating] = useState(initial?.rating || 5);
  const [comment, setComment] = useState(initial?.comment || '');
  const [verified, setVerified] = useState(initial?.is_verified_purchase !== false);
  const [createdAt, setCreatedAt] = useState(
    initial?.created_at
      ? initial.created_at.slice(0, 10)
      : new Date().toISOString().slice(0, 10)
  );
  const [existingMedia, setExistingMedia] = useState(initial?.media || []);
  const [newImages, setNewImages] = useState([]); // File[]
  const [newVideo, setNewVideo] = useState(null);  // File | null
  const [loading, setLoading] = useState(false);
  const imgInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setProductId(initial?.product_id || '');
      setUserName(initial?.user_name || '');
      setRating(initial?.rating || 5);
      setComment(initial?.comment || '');
      setVerified(initial?.is_verified_purchase !== false);
      setCreatedAt(initial?.created_at ? initial.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10));
      setExistingMedia(initial?.media || []);
      setNewImages([]);
      setNewVideo(null);
    }
  }, [open, initial]);

  const handleImagesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    const tooBig = files.find(f => f.size > 5 * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" excede 5 MB`);
      return;
    }
    setNewImages(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleVideoPicked = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (f.size > 30 * 1024 * 1024) {
      toast.error(`"${f.name}" excede 30 MB`);
      return;
    }
    setNewVideo(f);
    e.target.value = '';
  };

  const submit = async () => {
    if (!productId) { toast.error('Selecione um anúncio'); return; }
    if (!userName.trim()) { toast.error('Informe o nome do autor'); return; }
    if (!comment.trim()) { toast.error('Informe o comentário'); return; }
    setLoading(true);
    try {
      const createdAtIso = new Date(`${createdAt}T12:00:00Z`).toISOString();

      if (isEdit) {
        // Step 1: update text fields and media list (drop any removed existing items)
        await axios.put(`${API}/admin/reviews/${initial.review_id}`, {
          product_id: productId,
          user_name: userName.trim(),
          rating: Number(rating),
          comment: comment.trim(),
          is_verified_purchase: verified,
          created_at: createdAtIso,
          media: existingMedia,
        }, { withCredentials: true });

        // Step 2: upload any new files
        if (newImages.length > 0 || newVideo) {
          const fd = new FormData();
          newImages.forEach(f => fd.append('images', f));
          if (newVideo) fd.append('video', newVideo);
          await axios.post(`${API}/admin/reviews/${initial.review_id}/media`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        toast.success('Avaliação atualizada');
      } else {
        const fd = new FormData();
        fd.append('product_id', productId);
        fd.append('user_name', userName.trim());
        fd.append('rating', String(rating));
        fd.append('comment', comment.trim());
        fd.append('is_verified_purchase', String(verified));
        fd.append('created_at', createdAtIso);
        newImages.forEach(f => fd.append('images', f));
        if (newVideo) fd.append('video', newVideo);

        await axios.post(`${API}/admin/reviews`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Avaliação criada');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao salvar avaliação';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="review-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar avaliação' : 'Nova avaliação'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Anúncio</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="product-select">
                <SelectValue placeholder="Selecione o anúncio..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {products.map(p => (
                  <SelectItem key={p.product_id} value={p.product_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nome do autor</Label>
              <Input
                value={userName}
                onChange={e => setUserName(e.target.value)}
                placeholder="Ex: Camila S."
                data-testid="input-user-name"
              />
            </div>
            <div>
              <Label>Data</Label>
              <Input
                type="date"
                value={createdAt}
                onChange={e => setCreatedAt(e.target.value)}
                data-testid="input-created-at"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <Label>Avaliação (estrelas)</Label>
              <div className="mt-1.5">
                <StarsRow value={Number(rating)} onChange={setRating} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={verified}
                onChange={e => setVerified(e.target.checked)}
                data-testid="input-verified"
              />
              <span>Compra verificada</span>
            </label>
          </div>

          <div>
            <Label>Comentário</Label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={4}
              className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/30"
              placeholder="Escreva o texto da avaliação..."
              data-testid="input-comment"
            />
          </div>

          {/* Existing media */}
          {existingMedia.length > 0 && (
            <div>
              <Label className="text-xs text-gray-600">Mídias atuais</Label>
              <MediaPreview
                media={existingMedia}
                onRemove={(idx) => setExistingMedia(prev => prev.filter((_, i) => i !== idx))}
              />
            </div>
          )}

          {/* New uploads */}
          <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <UploadCloud className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Adicionar mídia</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => imgInputRef.current?.click()}
                data-testid="btn-pick-image"
              >
                <ImageIcon className="h-4 w-4 mr-1.5" /> Foto (até 5 MB)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => videoInputRef.current?.click()}
                data-testid="btn-pick-video"
              >
                <Video className="h-4 w-4 mr-1.5" /> Vídeo (até 30 MB)
              </Button>
              <input
                ref={imgInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleImagesPicked}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                hidden
                onChange={handleVideoPicked}
              />
            </div>

            {/* Pending previews */}
            {(newImages.length > 0 || newVideo) && (
              <div className="mt-3">
                <p className="text-[11px] text-gray-500 mb-1.5">
                  Para upload: {newImages.length} foto(s){newVideo ? ' + 1 vídeo' : ''}
                </p>
                <div className="flex flex-wrap gap-2">
                  {newImages.map((f, idx) => (
                    <div key={idx} className="relative w-16 h-16 rounded overflow-hidden border border-gray-200">
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setNewImages(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                  {newVideo && (
                    <div className="relative w-16 h-16 rounded overflow-hidden border border-gray-200 bg-gray-900 flex items-center justify-center">
                      <Play className="h-5 w-5 text-white" />
                      <button
                        type="button"
                        onClick={() => setNewVideo(null)}
                        className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="btn-cancel">
            Cancelar
          </Button>
          <Button onClick={submit} disabled={loading} className="bg-[#C41E3A] hover:bg-[#5a252c]" data-testid="btn-save">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</> : (isEdit ? 'Salvar alterações' : 'Criar avaliação')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReviewsPage() {
  const [searchParams] = useSearchParams();
  const initialProduct = searchParams.get('product') || 'all';
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState(initialProduct);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const productsMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.product_id] = p.name; });
    return map;
  }, [products]);

  const fetchProducts = async () => {
    try {
      const r = await axios.get(`${API}/products`, { withCredentials: true });
      const sorted = [...(r.data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProducts(sorted);
    } catch (e) {
      toast.error('Erro ao carregar produtos');
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = {};
      if (productFilter !== 'all') params.product_id = productFilter;
      if (ratingFilter !== 'all') params.rating = Number(ratingFilter);
      if (search) params.search = search;
      const r = await axios.get(`${API}/admin/reviews`, { withCredentials: true, params });
      setReviews(r.data.reviews || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchReviews(), search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilter, ratingFilter, search]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/admin/reviews/${deleting.review_id}`, { withCredentials: true });
      toast.success('Avaliação removida');
      setDeleting(null);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setProductFilter('all');
    setRatingFilter('all');
  };

  return (
    <div data-testid="admin-reviews-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Avaliações</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gerencie todas as avaliações dos anúncios</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="reviews-count">{total} avaliações</Badge>
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-[#C41E3A] hover:bg-[#5a252c]"
            data-testid="btn-new-review"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Nova avaliação
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 mb-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
          {(search || productFilter !== 'all' || ratingFilter !== 'all') && (
            <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-700 ml-auto" data-testid="btn-clear-filters">
              Limpar
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por autor ou texto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
              data-testid="filter-search"
            />
          </div>
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger data-testid="filter-product">
              <SelectValue placeholder="Todos os anúncios" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Todos os anúncios</SelectItem>
              {products.map(p => (
                <SelectItem key={p.product_id} value={p.product_id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ratingFilter} onValueChange={setRatingFilter}>
            <SelectTrigger data-testid="filter-rating">
              <SelectValue placeholder="Todas as estrelas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as estrelas</SelectItem>
              {ratingOptions.map(r => (
                <SelectItem key={r} value={String(r)}>{r} estrela{r > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Carregando...
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p className="font-medium">Nenhuma avaliação encontrada</p>
            <p className="text-sm mt-1">Ajuste os filtros ou crie uma nova.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Autor</TableHead>
                  <TableHead>Anúncio</TableHead>
                  <TableHead>Avaliação</TableHead>
                  <TableHead>Comentário</TableHead>
                  <TableHead>Mídia</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((r) => (
                  <TableRow key={r.review_id} data-testid={`row-${r.review_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm text-gray-900">{r.user_name}</p>
                        {r.is_verified_purchase && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 mt-0.5">
                            <Check className="h-3 w-3" /> Verificado
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-gray-700 max-w-[200px] truncate" title={productsMap[r.product_id] || r.product_id}>
                      {productsMap[r.product_id] || r.product_id}
                    </TableCell>
                    <TableCell><StarsRow value={r.rating} /></TableCell>
                    <TableCell className="max-w-[300px]">
                      <p className="text-xs text-gray-700 line-clamp-2">{r.comment}</p>
                    </TableCell>
                    <TableCell>
                      {(r.media?.length || 0) === 0 ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          {r.media.slice(0, 3).map((m, i) => (
                            <div key={i} className="w-8 h-8 rounded overflow-hidden border border-gray-200 bg-gray-50">
                              {m.type === 'video' ? (
                                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                  <Play className="h-3 w-3 text-white" />
                                </div>
                              ) : (
                                <img src={m.url} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                          ))}
                          {r.media.length > 3 && (
                            <span className="text-[10px] text-gray-500">+{r.media.length - 3}</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditing(r); setDialogOpen(true); }}
                          data-testid={`btn-edit-${r.review_id}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleting(r)}
                          data-testid={`btn-delete-${r.review_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <ReviewFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        products={products}
        initial={editing}
        onSaved={fetchReviews}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover avaliação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A avaliação e suas mídias serão apagadas permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sim, remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
